import React, { useCallback, useMemo } from 'react';
import { isNull, isUndefined } from 'lodash/fp';

import styles from './AccessGroups.module.scss';
import { clearAccessGroupFilters, getAccessGroupColumnSort, getAccessGroupFilters, AccessGroup, AccessGroupTableName, getAccessGroupDocumentGroups, getIsFetchingAccessGroupDocumentGroups } from './store';
import { AccessGroupDocumentsTable } from './AccessGroupDocumentsTable';
import { AccessGroupDocumentGroupsTable } from './AccessGroupDocumentGroupsTable';
import { useAppSelector, useAppDispatch } from '../../../hooks/react-redux';

const MODAL_HEADER_AND_PADDING = 55;
const TABLE_HEADER = 40;
const TABLE_TITLE = 40;
const SCROLLABLE_OFFSET = MODAL_HEADER_AND_PADDING + (TABLE_HEADER * 2) + (TABLE_TITLE * 2);
const NO_GROUPS_SCROLLABLE_OFFSET = 126;

interface AccessGroupDocumentsSectionProps {
    accessGroup: AccessGroup;
    updateGroup: (key: keyof AccessGroup, ids: number[]) => void;
    modalHeight: number;
}

export const AccessGroupDocumentsSection: React.FC<AccessGroupDocumentsSectionProps> = ({ accessGroup, updateGroup, modalHeight }) => {
    const { documentNameIds } = accessGroup;
    const dispatch = useAppDispatch();

    const documentColumnSort = useAppSelector(getAccessGroupColumnSort(AccessGroupTableName.DOCUMENT));
    const documentFilters = useAppSelector(getAccessGroupFilters(AccessGroupTableName.DOCUMENT));

    const documentGroups = useAppSelector(getAccessGroupDocumentGroups);
    const isFetchingDocumentGroups = useAppSelector(getIsFetchingAccessGroupDocumentGroups);
    const documentGroupsColumnSort = useAppSelector(getAccessGroupColumnSort(AccessGroupTableName.DOCUMENT_GROUP));
    const documentGroupsFilters = useAppSelector(getAccessGroupFilters(AccessGroupTableName.DOCUMENT_GROUP));
    const documentGroupsInAccessGroup = useMemo(() => documentGroups.filter(eachGroup => eachGroup.documentNameIds.every(entityId => documentNameIds.includes(entityId))), [documentGroups, documentNameIds]);

    const clearDocumentsFilters = useCallback(() => dispatch(clearAccessGroupFilters(AccessGroupTableName.DOCUMENT)), [dispatch]);
    const documentsFiltersExist = useMemo(() => !!Object.values(documentFilters).filter(({ text, dropdown }) => !isNull(dropdown) || !!text).length || !isUndefined(documentColumnSort), [documentFilters, documentColumnSort]);

    const clearDocumentGroupFilters = useCallback(() => dispatch(clearAccessGroupFilters(AccessGroupTableName.DOCUMENT_GROUP)), [dispatch]);
    const documentGroupFiltersExist = useMemo(() => !!Object.values(documentGroupsFilters).filter(({ text, dropdown }) => !isNull(dropdown) || !!text).length || !isUndefined(documentGroupsColumnSort), [documentGroupsFilters, documentGroupsColumnSort]);
    const showDocumentGroupsSection = useMemo(() => !isFetchingDocumentGroups && documentGroups.length > 0, [isFetchingDocumentGroups, documentGroups]);
    const docGroupsScrollableHeight = useMemo(() => showDocumentGroupsSection ? ((modalHeight - SCROLLABLE_OFFSET) / 3) : 0, [modalHeight, showDocumentGroupsSection]);
    const docsScrollableHeight = useMemo(() => showDocumentGroupsSection ? ((modalHeight - SCROLLABLE_OFFSET) * 2 / 3) : (modalHeight - NO_GROUPS_SCROLLABLE_OFFSET), [modalHeight, showDocumentGroupsSection]);

    return (
        <div className={styles.accessGroupSectionWrapper}>
            {showDocumentGroupsSection ? (
                <>
                    <div className={styles.tableTitleWrapper}>
                        <div className={styles.tableTitle}>Document Groups</div>
                        <div>({documentGroupsInAccessGroup.length} selected)</div>
                        {documentGroupFiltersExist &&
                            <div className={styles.clearAllFiltersWrapper}>
                                <button className={styles.clearAllFilters} onClick={clearDocumentGroupFilters} data-testid='access-group-document-groups-filter-button'>Clear All Filters</button>
                            </div>
                        }
                    </div>
                    <AccessGroupDocumentGroupsTable
                        documentNameIds={documentNameIds}
                        updateGroup={updateGroup}
                        scrollableHeight={docGroupsScrollableHeight}
                    />
                </>) : null}
            <div className={styles.tableTitleWrapper} style={showDocumentGroupsSection ? { marginTop: '10px' } : {}}>
                <div className={styles.tableTitle}>Document Types</div>
                <div>({documentNameIds.length} selected)</div>
                {documentsFiltersExist &&
                    <div className={styles.clearAllFiltersWrapper}>
                        <button className={styles.clearAllFilters} onClick={clearDocumentsFilters} data-testid='access-group-document-filter-button'>Clear All Filters</button>
                    </div>
                }
            </div>
            <AccessGroupDocumentsTable
                documentNameIds={documentNameIds}
                updateGroup={updateGroup}
                scrollableHeight={docsScrollableHeight}
            />
        </div>
    );
};
